// A patient's insurance cover: is it valid on a day, and which benefit year an instant falls
// in (docs/plans/phase2-private-insurance.md §3). Pure; days are East Africa calendar days.

import type { CalendarDay } from '../time.ts';
import { compareDays, daysInMonth, eatDayOf, startOfEatDay } from '../time.ts';

export const COVERAGE_RELATIONSHIPS = ['principal', 'dependant'] as const;
export type CoverageRelationship = (typeof COVERAGE_RELATIONSHIPS)[number];

export const COVER_STATUSES = ['active', 'ended', 'entered_in_error'] as const;
export type CoverStatus = (typeof COVER_STATUSES)[number];

export interface CoverDates {
  readonly status: CoverStatus;
  /** First day of cover, included. Null: not given (SHA covers may have none). */
  readonly periodStart: CalendarDay | null;
  /** Last day of cover, included. Null: no end date on the card (plan B2). */
  readonly periodEnd: CalendarDay | null;
}

export const COVER_VALIDITIES = ['valid', 'not_started', 'expired', 'ended'] as const;
export type CoverValidity = (typeof COVER_VALIDITIES)[number];

/** English words for each validity; the screens show these. */
export const COVER_VALIDITY_WORDS: Record<CoverValidity, string> = {
  valid: 'Valid',
  not_started: 'Not started yet',
  expired: 'Expired',
  ended: 'Ended',
};

/** Whether a cover can pay for care on `day`. Both ends of the period are included. */
export function coverValidOn(cover: CoverDates, day: CalendarDay): CoverValidity {
  if (cover.status !== 'active') return 'ended';
  if (cover.periodStart !== null && compareDays(day, cover.periodStart) < 0) return 'not_started';
  if (cover.periodEnd !== null && compareDays(day, cover.periodEnd) > 0) return 'expired';
  return 'valid';
}

/** The anniversary of `start` in `year`; 29 February falls on 28 February in other years. */
function anniversary(start: CalendarDay, year: number): CalendarDay {
  return { year, month: start.month, day: Math.min(start.day, daysInMonth(year, start.month)) };
}

export interface BenefitYear {
  /** Inclusive. */
  readonly from: Date;
  /** Exclusive. */
  readonly to: Date;
}

/**
 * The benefit year containing `at`: from an anniversary of the cover's start to the next,
 * at East Africa midnight. A cover with no start uses the calendar year (plan B4,
 * provisional: TODO(business)).
 */
export function benefitYear(
  cover: { readonly periodStart: CalendarDay | null },
  at: Date,
): BenefitYear {
  const today = eatDayOf(at);
  const anchor = cover.periodStart ?? { year: today.year, month: 1, day: 1 };
  let startYear = today.year;
  if (compareDays(today, anniversary(anchor, startYear)) < 0) startYear -= 1;
  return {
    from: startOfEatDay(anniversary(anchor, startYear)),
    to: startOfEatDay(anniversary(anchor, startYear + 1)),
  };
}
